import { useEffect, useState } from 'react'
import { Backspace } from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import { useI18n } from '@/i18n'

export interface PinPadProps {
  /** Digits required before onComplete fires (4–6). */
  length?: number
  onComplete: (pin: string) => void
  disabled?: boolean
  /** Shake + clear after a wrong PIN: bump this number. */
  errorSignal?: number
  label?: string
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'back'] as const

/** Large-key PIN entry. Digits are never rendered, only dots. Works with a hardware keyboard too. */
export function PinPad({ length = 4, onComplete, disabled, errorSignal = 0, label }: PinPadProps) {
  const { t } = useI18n()
  const [pin, setPin] = useState('')
  const [shake, setShake] = useState(false)
  const [prevSignal, setPrevSignal] = useState(errorSignal)

  if (errorSignal !== prevSignal) {
    setPrevSignal(errorSignal)
    setPin('')
    setShake(true)
  }

  useEffect(() => {
    if (!shake) return
    const timer = setTimeout(() => setShake(false), 400)
    return () => clearTimeout(timer)
  }, [shake])

  const press = (k: (typeof KEYS)[number]) => {
    if (disabled) return
    if (k === 'clear') return setPin('')
    if (k === 'back') return setPin((p) => p.slice(0, -1))
    setPin((p) => {
      if (p.length >= length) return p
      const next = p + k
      if (next.length === length) queueMicrotask(() => onComplete(next))
      return next
    })
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) press(e.key as (typeof KEYS)[number])
      else if (e.key === 'Backspace') press('back')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  return (
    <div className="flex w-full flex-col items-center gap-6">
      <div
        role="status"
        aria-live="polite"
        aria-label={label ? `${label}: ${pin.length} / ${length}` : `${pin.length} / ${length}`}
        className={cn('flex gap-4', shake && 'animate-shake')}
      >
        {Array.from({ length }, (_, i) => (
          <span
            key={i}
            aria-hidden="true"
            className={cn(
              'h-4 w-4 rounded-full border-2 transition-colors',
              i < pin.length ? 'border-ink bg-ink' : 'border-ink/30',
            )}
          />
        ))}
      </div>
      <div className="grid w-full max-w-[300px] grid-cols-3 gap-3">
        {KEYS.map((k) => {
          const isDigit = /^\d$/.test(k)
          return (
            <button
              key={k}
              type="button"
              disabled={disabled}
              onClick={() => press(k)}
              aria-label={k === 'back' ? t('pin.delete') : k === 'clear' ? t('pin.clear') : k}
              className={cn(
                'flex h-16 items-center justify-center rounded-card border text-ink transition-colors active:bg-ink/10 disabled:opacity-40',
                isDigit
                  ? 'border-rule bg-surface font-mono text-[26px] font-medium'
                  : 'border-transparent text-[13px] text-muted',
              )}
            >
              {k === 'back' ? (
                <Backspace size={26} aria-hidden="true" />
              ) : k === 'clear' ? (
                t('pin.clear')
              ) : (
                k
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
